import React from 'react';
import Input from './Input';
import PartTypeChip from './PartTypeChip';
import { formatAmount } from '../utils/format';
import { consumptionUnitLabel } from '../utils/paint';
import plusIcon from '../icons/plusB.svg';
import trashIcon from '../icons/trashW.svg';

const parseQuantity = (value) => parseFloat(String(value ?? '').replace(',', '.'));

// Internal cost of one paint/ingredient product used for the paint line: quantity x cost per unit
export const paintComponentCost = (component, product) => {
  const quantity = parseQuantity(component.quantity);
  return Number.isNaN(quantity) || !product?.unitCost ? 0 : quantity * product.unitCost;
};

/**
 * Splits the quote's "Peinture et ingrédients" order line into the paint/ingredient products the
 * storekeeper actually used, each with a quantity in its consumption unit (decimals for g/ml only). The
 * client is still billed the quote's estimate; this cost is internal. Only products stocked
 * as paint (with a consumption unit and a cost per unit) are offered. delivered holds the products
 * earlier BLs already used for this line, listed read-only above the new ones.
 */
const PaintLineEditor = ({ cartDetail, components = [], delivered = [], paintProducts = [], findProduct, onChange }) => {
  const updateComponent = (index, field, value) => {
    onChange(components.map((component, i) => (i === index ? { ...component, [field]: value } : component)));
  };
  const total = delivered.reduce((sum, detail) => sum + (detail.priceHT ?? 0), 0) + components.reduce(
    (sum, component) => sum + paintComponentCost(component, findProduct(component.productId)?.fullEntity),
    0
  );

  return (
    <div className="grid grid-cols-[1fr_auto_2fr] items-start gap-4">
      <div className="h-[88px] flex flex-col justify-center gap-1 bg-white rounded-[12px] px-5 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[#043F88] font-semibold text-[16px] truncate min-w-0">{cartDetail.designation}</p>
          <PartTypeChip line={cartDetail} />
        </div>
        <p className="text-[#707070] text-[14px] font-semibold">
          ESTIMÉ: <span className="text-[#043F88] ml-1">{formatAmount(cartDetail.priceHT ?? 0)}</span>
        </p>
      </div>
      <div className="w-4 h-4 mt-9" />
      <div className="min-h-[88px] flex flex-col justify-center gap-2 bg-white rounded-[12px] px-4 py-3 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
        {delivered.map((detail) => {
          const unit = consumptionUnitLabel(detail.product?.consumptionUnit);
          return (
            <div key={detail.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-2 bg-[#0AD69E12] border border-[#0AD69F] rounded-[12px] px-3 py-2">
              <div className="min-w-0">
                <p className="text-[12px] font-semibold text-[#707070]">{detail.product?.reference}</p>
                <p className="text-[#6B78F8] font-semibold text-[15px] truncate">{detail.product?.name}</p>
              </div>
              <div className="text-left">
                <p className="text-[12px] text-[#707070] font-semibold">QUANTITÉ</p>
                <p className="text-[#043F88] font-semibold text-[15px]">{detail.decimalQuantity ?? detail.quantity} {unit}</p>
              </div>
              <div className="text-left">
                <p className="text-[12px] text-[#707070] font-semibold">COÛT / {unit ? unit.toUpperCase() : 'UNITÉ'}</p>
                <p className="text-[#043F88] font-semibold text-[15px]">{formatAmount(detail.unitPrice ?? 0)}</p>
              </div>
              <div className="text-left">
                <p className="text-[12px] text-[#707070] font-semibold">COÛT</p>
                <p className="text-[#043F88] font-semibold text-[15px]">{formatAmount(detail.priceHT ?? 0)}</p>
              </div>
              <span className="text-[12px] font-semibold text-[#0AD69F]">Déjà livré</span>
            </div>
          );
        })}
        {components.map((component, index) => {
          const product = findProduct(component.productId)?.fullEntity;
          const unit = consumptionUnitLabel(product?.consumptionUnit);
          return (
            <div key={index} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-2">
              <Input
                type="dropdown"
                label="Produit"
                name={`paint-${cartDetail.id}-${index}`}
                value={component.productId || ''}
                onChange={(value) => updateComponent(index, 'productId', value)}
                options={paintProducts}
              />
              <Input
                type="text"
                label={`Quantité${unit ? ` (${unit})` : ''}`}
                name={`paint-qty-${cartDetail.id}-${index}`}
                value={component.quantity || ''}
                onChange={(value) => updateComponent(index, 'quantity', value)}
              />
              <div className="text-left">
                <p className="text-[12px] text-[#707070] font-semibold">COÛT / {unit ? unit.toUpperCase() : 'UNITÉ'}</p>
                <p className="text-[#043F88] font-semibold text-[15px]">{product?.unitCost != null ? formatAmount(product.unitCost) : '—'}</p>
              </div>
              <div className="text-left">
                <p className="text-[12px] text-[#707070] font-semibold">COÛT</p>
                <p className="text-[#043F88] font-semibold text-[15px]">{formatAmount(paintComponentCost(component, product))}</p>
              </div>
              <button
                type="button"
                onClick={() => onChange(components.filter((_, i) => i !== index))}
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#EF466F] rounded-[12px]"
                title="Retirer le produit"
              >
                <img src={trashIcon} alt="" className="w-4 h-4" />
              </button>
            </div>
          );
        })}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => onChange([...components, { productId: '', quantity: '' }])}
            className="inline-flex items-center gap-1 text-primary text-[14px] font-semibold"
          >
            <img src={plusIcon} alt="" className="w-4 h-4" /> Ajouter un produit
          </button>
          {(components.length > 0 || delivered.length > 0) && (
            <p className="text-[14px] font-semibold text-[#707070]">
              TOTAL RÉEL HT: <span className="text-[#043F88]">{formatAmount(total)}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaintLineEditor;
